import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Star, StarBorder } from '@mui/icons-material';
import { useFavoriteLists, toggleFavoriteList } from '../services/api';
import { useLogin } from '../contexts/LoginContext';

const FavoriteButton = ({ listId, userEmail, listName, variant = 'icon', className = '' }) => {
  const queryClient = useQueryClient();
  const { data: favoriteLists = [], isLoading } = useFavoriteLists(userEmail);
  const [isHovered, setIsHovered] = useState(false);
  const { triggerLogin } = useLogin();

  const isFavorited = favoriteLists.includes(listId);

  const { mutate: toggle, isLoading: isToggling } = useMutation({
    mutationFn: ({ listId, favorite }) => toggleFavoriteList(listId, favorite),
    onMutate: async ({ listId, favorite }) => {
      await queryClient.cancelQueries(['favoriteLists', userEmail]);
      const previousLists = queryClient.getQueryData(['favoriteLists', userEmail]);
      queryClient.setQueryData(['favoriteLists', userEmail], (old = []) =>
        favorite ? [...old, listId] : old.filter(id => id !== listId)
      );
      return { previousLists };
    },
    onError: (err, variables, context) => {
      queryClient.setQueryData(['favoriteLists', userEmail], context.previousLists);
    },
    onSettled: () => {
      queryClient.invalidateQueries(['favoriteLists', userEmail]);
    }
  });

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isToggling) return;
    if (!userEmail) {
      triggerLogin();
      return;
    }
    toggle({ listId, favorite: !isFavorited });
  };

  // Pill variant styles - use blue like NotificationBell for consistency
  const getPillStyles = (isActive, isLoggedOut) => {
    if (isLoggedOut) {
      // Interactive but shows login prompt on hover
      return 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 shadow-sm';
    }
    if (isActive) {
      return 'bg-blue-500 text-white hover:bg-blue-600 shadow-sm';
    }
    return 'bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-700 shadow-sm';
  };

  // Pill variant rendering
  if (variant === 'pill') {
    const pillBase = 'inline-flex items-center justify-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all cursor-pointer';

    if (isLoading || isToggling) {
      return (
        <button
          type="button"
          className={`${pillBase} ${getPillStyles(false, true)} animate-pulse ${className}`}
          disabled
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
          <StarBorder style={{ fontSize: 14 }} />
        </button>
      );
    }

    const displayName = listName || 'list';
    return (
      <button
        type="button"
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`${pillBase} ${getPillStyles(isFavorited, false)} ${className}`}
        title={isFavorited ? `Remove ${displayName} from My Lists` : `Add ${displayName} to My Lists`}
      >
        {isFavorited ? (
          <>
            <Star style={{ fontSize: 14 }} />
            <span>{isHovered ? `Remove ${displayName}` : 'In My Lists'}</span>
          </>
        ) : (
          <>
            <StarBorder style={{ fontSize: 14 }} />
            {isHovered && <span>Add {displayName} to My Lists</span>}
          </>
        )}
      </button>
    );
  }

  // Icon variant (default)
  if (!userEmail) return null;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isLoading || isToggling}
      className={`
        ml-2 p-1 rounded transition-colors duration-200
        ${isFavorited
          ? 'text-yellow-500 hover:text-yellow-600'
          : 'text-gray-300 hover:text-yellow-400'}
        ${(isLoading || isToggling) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      title={isFavorited ? `Remove ${listName || listId} from My Lists` : `Add ${listName || listId} to My Lists`}
    >
      {isFavorited ? (
        <Star style={{ fontSize: 18 }} />
      ) : (
        <StarBorder style={{ fontSize: 18 }} />
      )}
    </button>
  );
};

export default FavoriteButton;
